import debounce from 'lodash/debounce';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
  createContext,
  useContext,
} from 'react';

import { useCapability } from '@vimeo/core/hooks/use-capability';
import {
  getUserAiCreditsEstimate,
  GetUserAiCreditsEstimateFields,
} from '@vimeo/gctl-api/fetchers/user/ai_credits/estimate';
import { useGctlConfig } from '@vimeo/gctl-api/hooks/GctlProvider';
import { useGetVideoLazy } from '@vimeo/gctl-api/hooks/useVideo';
import type { User } from '@vimeo/gctl-api/models/components/schemas/user/User';
import { useViewer } from '@vimeo/viewer';

import { TranslationTypes } from '../constants';
import { TranslationMode } from '../types';

const DEBOUNCE_DELAY = 200;

interface TokensContext {
  amount: number;
  balance: number | undefined;
  isEnoughTokens: boolean;
  dubbingCreditsPerMin: number;
  subtitlingCreditsPerMin: number;
  duration: number;
  minutes: number;
  langCnt: number;
  reloadTokens: VoidFunction;
  initCreditsPerMin: VoidFunction;
  setCurrentService: Dispatch<SetStateAction<string>>;
  resetTokensContext: VoidFunction;
  setLangCnt: (langCnt: number) => void;
  calculateTokensAmount: (serviceName: string, jobNumbers: number) => Promise<number>;
  isOwner: boolean;
  hasEntitlementAiCredits: boolean;
  isBlockingModalOpen: boolean;
  setIsBlockingModalOpen: Dispatch<SetStateAction<boolean>>;
}

const TokensContext = createContext<TokensContext | null>(null);

interface TokenVideoData {
  duration: number;
  quota: User['aiCreditsQuota'];
  ownerUri: string;
}

export const TokensContextProvider = ({
  children,
  videoId,
}: {
  children: ReactNode;
  videoId: number;
}) => {
  const [langCnt, setLangCnt] = useState<number>(0);
  const [videoData, setVideoData] = useState<TokenVideoData | null>(null);
  const [currentService, setCurrentService] = useState<string>(TranslationTypes.DUBBING);
  const [dubbingCreditsPerMin, setDubbingCreditsPerMin] = useState<number>(0);
  const [subtitlingCreditsPerMin, setSubtitlingCreditsPerMin] = useState<number>(0);
  const [isBlockingModalOpen, setIsBlockingModalOpen] = useState<boolean>(false);
  const [currentAmount, setCurrentAmount] = useState<number>(0);
  const [minutes, setMinutes] = useState<number>(0);

  const { baseUrl, jwt } = useGctlConfig();
  const viewer = useViewer();
  const userId: number = viewer?.teamUser?.ownerId ?? (viewer?.user?.id as number);

  const [getVideoData, { error, loading, data }] = useGetVideoLazy();

  useEffect(() => {
    if (!loading && data && data.duration != null) {
      setVideoData({
        duration: data.duration,
        quota: data.user?.aiCreditsQuota,
        ownerUri: data.user?.uri,
      });
    }
  }, [error, loading, data]);

  const { capabilities } = useCapability(['entitlementAiCredits'], videoData?.ownerUri);

  const getCreditsData = useCallback(
    async ({
      serviceName,
      serviceUnits,
      jobNumbers,
      select,
    }: {
      serviceName: TranslationMode;
      serviceUnits: number;
      jobNumbers: number;
      select: GetUserAiCreditsEstimateFields;
    }) => {
      return await getUserAiCreditsEstimate({
        where: { userId },
        headers: {
          Authorization: `jwt ${jwt}`,
          'Content-Type': 'application/json',
        },
        baseUrl,
        query: { serviceName, serviceUnits, jobNumbers },
        select,
      });
    },
    [baseUrl, jwt, userId],
  );

  const initCreditsPerMin = useCallback(() => {
    getCreditsData({
      serviceName: TranslationTypes.DUBBING as TranslationMode,
      serviceUnits: 1,
      jobNumbers: 1,
      select: ['creditsPerMinute'],
    }).then(({ creditsPerMinute }) => setDubbingCreditsPerMin(creditsPerMinute));

    getCreditsData({
      serviceName: TranslationTypes.SUBTITLING as TranslationMode,
      serviceUnits: 1,
      jobNumbers: 1,
      select: ['creditsPerMinute'],
    }).then(({ creditsPerMinute }) => setSubtitlingCreditsPerMin(creditsPerMinute));
  }, [getCreditsData]);

  const reloadTokens = useCallback(() => {
    getVideoData({
      where: { videoId },
      select: ['duration', 'user.uri', 'user.aiCreditsQuota'],
    });
  }, [videoId, getVideoData]);

  const resetTokensContext = useCallback(() => {
    setLangCnt(0);
  }, []);

  const calculateTokensAmount = useCallback(
    async (serviceName: string, jobNumbers: number) => {
      try {
        const data = await getCreditsData({
          jobNumbers,
          serviceName: serviceName as TranslationMode,
          serviceUnits: Number(videoData?.duration),
          select: ['creditsAmount', 'minutes'],
        });
        return Number(data?.creditsAmount);
      } catch (error) {
        return 0;
      }
    },
    [getCreditsData, videoData?.duration],
  );

  const fetchAmount = useMemo(
    () =>
      debounce(async ({ serviceName, serviceUnits, jobNumbers }) => {
        if (jobNumbers > 0) {
          try {
            const { creditsAmount, minutes } = await getCreditsData({
              serviceName,
              serviceUnits,
              jobNumbers,
              select: ['creditsAmount', 'minutes'],
            });

            setCurrentAmount(creditsAmount);
            setMinutes(minutes);
          } catch (error) {
            // do nothing, later could be added re-fetch
          }
        } else {
          setCurrentAmount(0);
          setMinutes(0);
        }
      }, DEBOUNCE_DELAY),
    [getCreditsData],
  );

  const currentBalance = useMemo(() => {
    return videoData?.quota?.remaining;
  }, [videoData]);

  const isEnoughTokens = useMemo(() => {
    if (videoData?.duration == null) {
      return false;
    }

    return currentAmount <= (currentBalance ?? 0);
  }, [videoData?.duration, currentAmount, currentBalance]);

  const isOwner = useMemo(() => {
    return Boolean(viewer?.user?.uri === videoData?.ownerUri);
  }, [videoData?.ownerUri, viewer?.user?.uri]);

  useEffect(() => {
    //reset current amount on service type change
    setCurrentAmount(0);
  }, [currentService]);

  useEffect(() => {
    if (videoData?.duration != null) {
      fetchAmount({
        serviceName: currentService as TranslationMode,
        serviceUnits: videoData.duration,
        jobNumbers: langCnt,
      });
    } else {
      if (langCnt === 0) {
        fetchAmount.cancel();
      }
    }
  }, [currentService, fetchAmount, langCnt, videoData?.duration]);

  return (
    <TokensContext.Provider
      value={{
        hasEntitlementAiCredits: Boolean(capabilities?.entitlementAiCredits),
        amount: langCnt ? currentAmount : 0,
        isEnoughTokens,
        balance: currentBalance,
        dubbingCreditsPerMin,
        subtitlingCreditsPerMin,
        minutes,
        langCnt,
        duration: videoData?.duration ?? 0,
        setLangCnt,
        reloadTokens,
        initCreditsPerMin,
        setCurrentService,
        resetTokensContext,
        calculateTokensAmount,
        isBlockingModalOpen,
        setIsBlockingModalOpen,
        isOwner,
      }}
    >
      {children}
    </TokensContext.Provider>
  );
};

export const useTokensContext = () => {
  const context = useContext(TokensContext);

  if (!context) {
    throw new Error('useTokensContext must be used within a TokensContextProvider');
  }
  return context;
};
